import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMessages, markMessageAsRead, sendMessage, getUsers, getConversations, Message, User, Conversation } from '../services/messages';
import './MessagesDropdown.css';

interface MessagesDropdownProps {
  userRole?: string;
  userId?: number;
}

const MessagesDropdown: React.FC<MessagesDropdownProps> = ({ userRole, userId }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  /** Solo para admin/editor mensajería: cantidad de chats (conversaciones) con mensajes sin leer */
  const [unreadConversationsCount, setUnreadConversationsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [adminId, setAdminId] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isAdmin = userRole === 'admin';

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowSendModal(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Cargar contador de mensajes no leídos al montar y periódicamente
  const loadUnreadCount = async () => {
    try {
      const response = await getMessages();
      setUnreadCount(response.unreadCount);
    } catch (error) {

    }
  };

  // Cargar al montar: si admin/editor mensajería, conversaciones para badge de "chats sin leer"; si no, mensajes
  useEffect(() => {
    if (isAdmin) {
      loadConversations();
    } else {
      loadUnreadCount();
    }
  }, [isAdmin]);

  // Actualizar periódicamente cuando el dropdown está cerrado
  useEffect(() => {
    if (!isOpen) {
      const interval = setInterval(() => {
        if (isAdmin) {
          loadConversations();
        } else {
          loadUnreadCount();
        }
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [isOpen, isAdmin]);

  // Cargar conversaciones o mensajes cuando se abre el dropdown
  useEffect(() => {
    if (isOpen) {
      if (isAdmin) {
        loadConversations();
      } else {
        loadMessages();
        findAdminId();
      }
    }
  }, [isOpen, isAdmin]);

  // Función para encontrar el ID del admin
  const findAdminId = async () => {
    try {
      // Buscar en los mensajes recibidos
      const response = await getMessages();
      const adminMessage = response.messages.find(msg => msg.fromUser.role === 'admin');
      if (adminMessage) {
        setAdminId(adminMessage.fromUser.id);
      } else {
        // Si no hay mensajes, intentar obtener usuarios (aunque normalmente solo admins pueden)
        // O podemos crear un endpoint específico para obtener el admin
        // Por ahora, intentamos con getUsers aunque puede fallar
        try {
          const usersResponse = await getUsers();
          const admin = usersResponse.users.find(u => u.role === 'admin');
          if (admin) {
            setAdminId(admin.id);
          }
        } catch (err) {
          // Si falla, no hay problema, el usuario no podrá contactar a soporte hasta recibir un mensaje
        }
      }
    } catch (error) {

    }
  };

  // Cargar usuarios cuando se abre el modal de enviar mensaje (solo para admins)
  useEffect(() => {
    if (showSendModal && isAdmin) {
      loadUsers();
    }
  }, [showSendModal, isAdmin]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await getConversations();
      setConversations(response.conversations || []);
      const convs = response.conversations || [];
      const chatsWithUnread = convs.filter(c => (c.unreadCount ?? 0) > 0).length;
      setUnreadConversationsCount(chatsWithUnread);
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const response = await getMessages();
      setMessages(response.messages);
      setUnreadCount(response.unreadCount); // Actualizar contador también cuando se cargan los mensajes
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      setUsersError(null);
      const response = await getUsers();
      setUsers(response.users.filter(u => u.id !== userId)); // Excluir el usuario actual
    } catch (error: any) {

      setUsersError(error.message || 'Error al cargar usuarios');
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleMarkAsRead = async (messageId: number) => {
    try {
      await markMessageAsRead(messageId);
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, read: true } : msg
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {

    }
  };

  const handleSendMessage = async () => {
    if (!selectedUserId || !messageText.trim()) {
      return;
    }

    try {
      setSending(true);
      await sendMessage({
        toUserId: selectedUserId,
        message: messageText.trim()
      });
      setMessageText('');
      setSelectedUserId(null);
      setShowSendModal(false);
      // Recargar mensajes para mostrar el mensaje enviado (si el admin se envía a sí mismo)
      if (selectedUserId === userId) {
        loadMessages();
      }
    } catch (error) {

      alert('Error al enviar mensaje. Por favor, intenta nuevamente.');
    } finally {
      setSending(false);
    }
  };

  // Función helper para convertir fecha de BD a GMT-3 (sumar 3 horas)
  const parseGMT3Date = (dateString: string): Date => {
    // La BD guarda en UTC (o timezone que está 3 horas atrás)
    // Para mostrar en GMT-3 (Argentina), hay que SUMAR 3 horas
    // Ejemplo: BD tiene 15:00 → mostrar 18:00
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      // Si no tiene timezone, asumir UTC y sumar 3 horas para GMT-3
      // Formato esperado: "2024-01-29 15:00:00" o "2024-01-29T15:00:00"
      const dateTime = dateString.replace(' ', 'T');
      // Crear Date interpretando como UTC
      const utcDate = new Date(dateTime + 'Z');
      // SUMAR 3 horas para convertir a GMT-3 (Argentina)
      const gmt3Date = new Date(utcDate.getTime() + (3 * 60 * 60 * 1000));
      return gmt3Date;
    }
    // Si ya tiene timezone, convertir a Date y sumar 3 horas
    const date = new Date(dateString);
    return new Date(date.getTime() + (3 * 60 * 60 * 1000));
  };

  const formatDate = (dateString: string) => {
    const date = parseGMT3Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return 'Hoy';
    } else if (days === 1) {
      return 'Ayer';
    } else if (days < 7) {
      return `Hace ${days} días`;
    } else {
      return date.toLocaleDateString('es-AR', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        timeZone: 'America/Argentina/Buenos_Aires' // GMT-3
      });
    }
  };

  // Función helper para extraer solo el primer nombre
  const getFirstName = (fullName?: string): string => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  return (
    <div className="messages-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        className="messages-dropdown-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Mensajes"
      >
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        {((isAdmin ? unreadConversationsCount : unreadCount) > 0) && (
          <span className="messages-badge">{isAdmin ? unreadConversationsCount : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="messages-dropdown-menu">
          <div className="messages-header">
            <h3>Mensajes</h3>
            <div className="messages-header-actions">
            </div>
          </div>

          <div className="messages-list">
            {loading ? (
              <div className="messages-loading">Cargando mensajes...</div>
            ) : isAdmin ? (
              // Para admin: mostrar conversaciones agrupadas por usuario
              conversations.length === 0 ? (
                <div className="messages-empty">No hay conversaciones</div>
              ) : (
                <>
                  {conversations.map((conv) => (
                    <div
                      key={conv.userId}
                      className={`message-item ${conv.unreadCount > 0 ? 'unread' : ''}`}
                    >
                      <div 
                        className="message-content"
                        onClick={() => {
                          setIsOpen(false);
                          // Guardar asUserId en sessionStorage antes de abrir la nueva pestaña
                          sessionStorage.setItem('chatsPage_asUserId', conv.userId.toString());
                          const url = `${window.location.origin}/soporte?asUserId=${conv.userId}`;

                          window.open(url, '_blank');
                        }}
                      >
                        <div className="message-header">
                          <span className="message-from">
                            {conv.role === 'admin'
                              ? conv.name && conv.name !== conv.email
                                ? `${getFirstName(conv.name)} (Soporte)`
                                : 'Soporte'
                              : conv.name && conv.name !== conv.email
                                ? getFirstName(conv.name)
                                : conv.email}
                          </span>
                          <span className="message-date">{formatDate(conv.lastMessageDate)}</span>
                        </div>
                        <div className="message-text">
                          {conv.lastMessage || 'Sin mensajes'}
                        </div>
                        {conv.unreadCount > 0 && (
                          <div className="message-unread-badge">{conv.unreadCount}</div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div 
                    className="messages-view-all-link"
                    onClick={() => {
                      setIsOpen(false);
                      window.open('/soporte', '_blank');
                    }}
                  >
                    Ver todos los chats
                  </div>
                </>
              )
            ) : (
              // Para usuarios normales: mostrar mensajes individuales
              messages.length === 0 ? (
                <div className="messages-empty">No hay mensajes</div>
              ) : (
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`message-item ${!message.read ? 'unread' : ''}`}
                    >
                      <div 
                        className="message-content"
                        onClick={() => {
                          if (!message.read) {
                            handleMarkAsRead(message.id);
                          }
                          setIsOpen(false);
                          // Guardar asUserId en sessionStorage antes de abrir la nueva pestaña
                          sessionStorage.setItem('chatsPage_asUserId', message.fromUser.id.toString());
                          const url = `${window.location.origin}/soporte?asUserId=${message.fromUser.id}`;

                          window.open(url, '_blank');
                        }}
                      >
                        <div className="message-header">
                          <span className="message-from">
                            {message.fromUser.role === 'admin'
                              ? message.fromUser.name && message.fromUser.name !== message.fromUser.email
                                ? `${getFirstName(message.fromUser.name)} (Soporte)`
                                : 'Soporte'
                              : message.fromUser.name && message.fromUser.name !== message.fromUser.email
                                ? getFirstName(message.fromUser.name)
                                : message.fromUser.email}
                          </span>
                          <span className="message-date">{formatDate(message.createdAt)}</span>
                        </div>
                        <div className="message-text">
                          {message.message && message.message.trim() 
                            ? message.message 
                            : message.attachmentType === 'image' 
                              ? 'foto' 
                              : message.attachmentType 
                                ? 'archivo' 
                                : ''}
                        </div>
                        {!message.read && <div className="message-unread-indicator"></div>}
                      </div>
                    </div>
                  ))}
                </>
              )
            )}
          </div>
        </div>
      )}

      {showSendModal && isAdmin && (
        <div className="messages-modal-overlay" onClick={() => setShowSendModal(false)}>
          <div className="messages-modal" onClick={(e) => e.stopPropagation()}>
            <div className="messages-modal-header">
              <h3>Enviar mensaje</h3>
              <button
                type="button"
                className="messages-modal-close"
                onClick={() => setShowSendModal(false)}
              >
                ×
              </button>
            </div>
            <div className="messages-modal-body">
              <label>
                Usuario:
                {loadingUsers ? (
                  <div className="messages-loading-users">Cargando usuarios...</div>
                ) : usersError ? (
                  <div className="messages-error-users">{usersError}</div>
                ) : (
                  <select
                    value={selectedUserId || ''}
                    onChange={(e) => setSelectedUserId(Number(e.target.value))}
                    className="messages-user-select"
                  >
                    <option value="">Seleccionar usuario</option>
                    {users.length === 0 ? (
                      <option value="" disabled>No hay usuarios disponibles</option>
                    ) : (
                      users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.email}
                        </option>
                      ))
                    )}
                  </select>
                )}
              </label>
              <label>
                Mensaje:
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  className="messages-textarea"
                  rows={5}
                  placeholder="Escribe tu mensaje aquí..."
                />
              </label>
            </div>
            <div className="messages-modal-footer">
              <button
                type="button"
                className="messages-modal-cancel"
                onClick={() => setShowSendModal(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="messages-modal-send"
                onClick={handleSendMessage}
                disabled={!selectedUserId || !messageText.trim() || sending}
              >
                {sending ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesDropdown;
